import React, { useMemo, useState, useEffect, useRef } from "react";
import { ExternalLink } from "lucide-react";
import { getYahooUrl } from "@/lib/yahooFinance";

function getArrow(netScore) {
  if (netScore >= 4) return '\u25B2\u25B2';
  if (netScore >= 1) return '\u25B2';
  if (netScore === 0) return '\u2014';
  if (netScore >= -3) return '\u25BC';
  return '\u25BC\u25BC';
}

function getColor(netScore) {
  if (netScore > 0) return '#22c55e';
  if (netScore < 0) return '#ef4444';
  return '#94a3b8';
}

function getBg(netScore) {
  if (netScore > 0) return 'rgba(34, 197, 94, 0.1)';
  if (netScore < 0) return 'rgba(239, 68, 68, 0.1)';
  return 'rgba(148, 163, 184, 0.06)';
}

function getBorder(netScore) {
  if (netScore > 0) return 'rgba(34, 197, 94, 0.25)';
  if (netScore < 0) return 'rgba(239, 68, 68, 0.25)';
  return '#30363d';
}

export default function InstrumentTags({ aggregation }) {
  const instrumentScores = aggregation?.instrument_scores || {};
  const [flashKeys, setFlashKeys] = useState(new Set());
  const [hoveredSymbol, setHoveredSymbol] = useState(null);
  const prevScores = useRef({});

  const sorted = useMemo(() => {
    return Object.entries(instrumentScores)
      .sort((a, b) => Math.abs(b[1].net_score) - Math.abs(a[1].net_score));
  }, [instrumentScores]);

  useEffect(() => {
    const newFlash = new Set();
    for (const [symbol, data] of Object.entries(instrumentScores)) {
      const prev = prevScores.current[symbol];
      if (prev && prev.net_score !== data.net_score) {
        newFlash.add(symbol);
      }
    }
    if (newFlash.size > 0) {
      setFlashKeys(newFlash);
      const timer = setTimeout(() => setFlashKeys(new Set()), 600);
      return () => clearTimeout(timer);
    }
    prevScores.current = { ...instrumentScores };
  }, [instrumentScores]);

  if (sorted.length === 0) return null;

  return (
    <div
      data-testid="instrument-tags-panel"
      className="rounded-[10px] border overflow-hidden"
      style={{ backgroundColor: '#161b22', borderColor: '#30363d' }}
    >
      <div
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: '#30363d' }}
      >
        <span
          className="font-mono text-xs tracking-[0.12em] uppercase"
          style={{ color: '#8b949e' }}
        >
          Instruments
        </span>
        <span
          className="font-mono text-[10px]"
          style={{ color: '#484f58' }}
        >
          {sorted.length} tracked
        </span>
      </div>
      <div className="px-3 py-2 space-y-1 max-h-[300px] overflow-y-auto">
        {sorted.map(([symbol, data]) => {
          const color = getColor(data.net_score);
          const arrow = getArrow(data.net_score);
          const isFlashing = flashKeys.has(symbol);
          const isHovered = hoveredSymbol === symbol;
          const yahooUrl = getYahooUrl(symbol);

          return (
            <a
              key={symbol}
              href={yahooUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instrument-tag-item"
              className="flex items-center justify-between px-2.5 py-1.5 rounded-[6px] transition-colors duration-150 no-underline"
              style={{
                backgroundColor: getBg(data.net_score),
                border: `1px solid ${getBorder(data.net_score)}`,
                animation: isFlashing ? 'cw-flash 600ms ease-out' : 'none',
                textDecoration: 'none',
              }}
              title={`View ${symbol} on Yahoo Finance`}
              onMouseEnter={() => setHoveredSymbol(symbol)}
              onMouseLeave={() => setHoveredSymbol(null)}
            >
              <span className="font-mono text-xs font-medium" style={{ color }}>
                {arrow} {symbol}
              </span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs font-semibold" style={{ color }}>
                  {data.net_score > 0 ? '+' : ''}{data.net_score}
                </span>
                <span className="font-mono text-[10px]" style={{ color: '#484f58' }}>
                  {data.post_count}x
                </span>
                {isHovered && (
                  <ExternalLink size={9} style={{ color, opacity: 0.5 }} />
                )}
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
